import { create } from 'zustand';
import { DEFAULT_THEME, THEME_STORAGE_KEY, isKnownTheme } from './themes.ts';

/**
 * The chosen theme.
 *
 * Persisted to localStorage, and applied by setting one attribute on <html> —
 * the token sets in themes.css do the rest. No JS writes colours.
 *
 * localStorage is per-browser, which for a household where everyone has their
 * own devices is effectively per-person. When it needs to follow someone
 * across devices, the upgrade is small and contained: add a `theme` column to
 * the users table, return it from /api/auth/me, and have `setTheme` also PATCH
 * it. Nothing else in the app would change.
 */
interface ThemeState {
  theme: string;
  setTheme(id: string): void;
}

function read(): string {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isKnownTheme(stored) ? stored! : DEFAULT_THEME;
  } catch {
    // Private mode, or storage blocked. A default is always fine.
    return DEFAULT_THEME;
  }
}

export function applyTheme(id: string): void {
  document.documentElement.dataset.theme = id;
}

export const useTheme = create<ThemeState>((set) => ({
  theme: read(),
  setTheme: (id) => {
    if (!isKnownTheme(id)) return;
    applyTheme(id);
    try { localStorage.setItem(THEME_STORAGE_KEY, id); } catch { /* not fatal */ }
    set({ theme: id });
  },
}));
